import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const portfolioItems = [
  {
    title: "LinkedIn Profile Overhaul",
    category: "Professional Services",
    tech: "Canva, LinkedIn",
    result: "60% increase in profile views, improved inbound job inquiries.",
    icon: FiIcons.FiLinkedin,
    image: "https://images.unsplash.com/photo-1616469829581-73993eb86b02?auto=format&fit=crop&q=80&w=800",
    stat: "60%",
    statLabel: "More Profile Views",
  },
  {
    title: "Resume Transformation",
    category: "Career & Resume (IT Specialist)",
    tech: "Google Docs, Adobe PDF",
    result: "Candidate secured multiple interviews within a week.",
    icon: FiIcons.FiFileText,
    image: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?auto=format&fit=crop&q=80&w=800",
    stat: "7 Days",
    statLabel: "To First Interview",
  },
  {
    title: "Social Media Launch",
    category: "Brand Building (Local Restaurant)",
    tech: "Buffer, Instagram, Facebook",
    result: "Generated viral posts and boosted social engagement by 30%.",
    icon: FiIcons.FiTrendingUp,
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&q=80&w=800",
    stat: "30%",
    statLabel: "Engagement Boost",
  },
];

const Portfolio = () => {
  return (
    <section id="portfolio" className="py-24 bg-white overflow-hidden">
      <div className="container mx-auto px-6 max-w-7xl">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-sm font-bold text-brand-accent uppercase tracking-widest mb-2">Proven Results</h2>
          <h3 className="text-3xl md:text-5xl font-extrabold text-brand-secondary">Our Portfolio</h3>
          <motion.div
            className="h-1 bg-brand-accent mx-auto mt-6 rounded-full"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {portfolioItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              whileHover={{ y: -10 }}
              className="group rounded-2xl overflow-hidden shadow-lg border border-gray-100 cursor-default"
              style={{ transition: 'box-shadow 0.3s ease' }}
            >
              <div className="relative h-64 overflow-hidden">
                <motion.div
                  className="absolute inset-0 bg-brand-secondary/60 z-10"
                  whileHover={{ backgroundColor: 'rgba(2,19,81,0.3)' }}
                  transition={{ duration: 0.4 }}
                />
                <motion.img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.12 }}
                  transition={{ duration: 0.7, ease: 'easeOut' }}
                />
                <div className="absolute top-4 left-4 z-20 bg-white p-2 rounded-lg shadow-md">
                  <SafeIcon icon={item.icon} className="text-2xl text-brand-secondary" />
                </div>
                <div className="absolute bottom-4 left-4 z-20">
                  <span className="px-3 py-1 bg-brand-accent text-brand-secondary text-xs font-bold rounded-full uppercase tracking-wider">
                    {item.category}
                  </span>
                </div>
                <motion.div
                  className="absolute top-4 right-4 z-20 bg-brand-secondary/90 backdrop-blur-sm px-3 py-2 rounded-xl text-center border border-brand-accent/30"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 + 0.4 }}
                >
                  <motion.p
                    className="text-brand-accent font-extrabold text-lg leading-none"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity, delay: index * 0.5 }}
                  >
                    {item.stat}
                  </motion.p>
                  <p className="text-white/70 text-xs mt-1">{item.statLabel}</p>
                </motion.div>
              </div>

              <div className="p-8 bg-white">
                <h4 className="text-xl font-bold text-brand-secondary mb-3">{item.title}</h4>
                <div className="mb-4">
                  <p className="text-sm text-gray-500 uppercase tracking-wide font-semibold mb-1">Tools Used:</p>
                  <p className="text-gray-700 font-medium">{item.tech}</p>
                </div>
                <motion.div
                  className="bg-blue-50 p-4 rounded-xl border border-blue-100"
                  whileHover={{ backgroundColor: '#eff6ff', borderColor: '#3BDFF8' }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="text-sm text-brand-secondary font-semibold flex items-start">
                    <SafeIcon icon={FiIcons.FiAward} className="text-brand-accent mr-2 mt-0.5 flex-shrink-0" />
                    <span>{item.result}</span>
                  </p>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;